import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  AutoAwesome as GenerateIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Folder as FolderIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { questionBasesApi, fileSourcesApi } from '../services/api';

function QuestionBaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [base, setBase] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [allFileSources, setAllFileSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    num_questions: 10,
    difficulty: 'mixed',
    custom_instructions: '',
  });
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);
  
  // Settings dialog state
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    name: '',
    description: '',
    file_source_ids: [],
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Question dialog state
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question: '',
    expected_answer: '',
    context: '',
    difficulty: 'medium',
  });
  const [savingQuestion, setSavingQuestion] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [baseRes, questionsRes, sourcesRes] = await Promise.all([
        questionBasesApi.get(id),
        questionBasesApi.listQuestions(id),
        fileSourcesApi.list(),
      ]);
      setBase(baseRes.data);
      setQuestions(questionsRes.data);
      setAllFileSources(sourcesRes.data);
      
      // Pre-fill settings form
      setSettingsForm({
        name: baseRes.data.name,
        description: baseRes.data.description || '',
        file_source_ids: baseRes.data.file_sources?.map(fs => fs.id) || [],
      });
      
      // Pre-fill generate form with saved prompt
      if (baseRes.data.generation_prompt) {
        setGenerateForm(prev => ({
          ...prev,
          custom_instructions: baseRes.data.generation_prompt,
        }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuestions = async () => {
    setGenerating(true);
    setError(null);
    
    try {
      await questionBasesApi.generateQuestions(id, generateForm);
      setGenerateDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error generating questions:', error);
      setError(error.response?.data?.detail || 'Erro ao gerar perguntas. Verifique as credenciais AWS e as fontes de dados vinculadas.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settingsForm.name.trim()) {
      setError('O nome é obrigatório');
      return;
    }

    setSavingSettings(true);
    setError(null);

    try {
      await questionBasesApi.update(id, settingsForm);
      setSettingsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Erro ao salvar configurações');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteAllQuestions = async () => {
    if (!window.confirm('Tem certeza que deseja excluir todas as perguntas?')) return;
    
    try {
      await questionBasesApi.deleteAllQuestions(id);
      loadData();
    } catch (error) {
      console.error('Error deleting questions:', error);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta pergunta?')) return;
    
    try {
      await questionBasesApi.deleteQuestion(id, questionId);
      loadData();
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const handleOpenCreateQuestion = () => {
    setEditingQuestion(null);
    setQuestionForm({
      question: '',
      expected_answer: '',
      context: '',
      difficulty: 'medium',
    });
    setQuestionDialogOpen(true);
  };

  const handleOpenEditQuestion = (question, e) => {
    e.stopPropagation();
    setEditingQuestion(question);
    setQuestionForm({
      question: question.question,
      expected_answer: question.expected_answer || '',
      context: question.context || '',
      difficulty: question.difficulty || 'medium',
    });
    setQuestionDialogOpen(true);
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.question.trim()) {
      setError('A pergunta é obrigatória');
      return;
    }

    setSavingQuestion(true);
    setError(null);

    try {
      if (editingQuestion) {
        await questionBasesApi.updateQuestion(id, editingQuestion.id, questionForm);
      } else {
        await questionBasesApi.createQuestion(id, {
          ...questionForm,
          question_base_id: parseInt(id),
        });
      }
      setQuestionDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving question:', error);
      setError('Erro ao salvar pergunta');
    } finally {
      setSavingQuestion(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!base) {
    return (
      <Box>
        <Alert severity="error">Base de perguntas não encontrada</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/question-bases')} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/question-bases')}>
          <BackIcon />
        </IconButton>
        <Box flexGrow={1}>
          <Typography variant="h4">{base.name}</Typography>
          <Typography color="textSecondary">{base.description || 'Sem descrição'}</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setSettingsDialogOpen(true)}
        >
          Configurações
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* File Sources Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Fontes de Dados Vinculadas
          </Typography>
          {base.file_sources && base.file_sources.length > 0 ? (
            <Box display="flex" gap={1} flexWrap="wrap">
              {base.file_sources.map((fs) => (
                <Chip
                  key={fs.id}
                  icon={<FolderIcon />}
                  label={`${fs.name} (${fs.files_count || 0} arquivos)`}
                  variant="outlined"
                  onClick={() => navigate(`/file-sources/${fs.id}`)}
                />
              ))}
            </Box>
          ) : (
            <Typography color="textSecondary" variant="body2">
              Nenhuma fonte de dados vinculada. Configure nas configurações.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Questions Section */}
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">Perguntas ({questions.length})</Typography>
          <Box display="flex" gap={2}>
            {questions.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => window.open(questionBasesApi.exportQuestions(id, 'json'), '_blank')}
                >
                  Exportar JSON
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => window.open(questionBasesApi.exportQuestions(id, 'csv'), '_blank')}
                >
                  Exportar CSV
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteAllQuestions}
                >
                  Excluir Todas
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateQuestion}
            >
              Adicionar Pergunta
            </Button>
            <Button
              variant="contained"
              startIcon={<GenerateIcon />}
              onClick={() => setGenerateDialogOpen(true)}
              disabled={!base.file_sources?.length}
            >
              Gerar Perguntas
            </Button>
          </Box>
        </Box>

        {questions.length > 0 ? (
          <Box>
            {questions.map((question, index) => (
              <Accordion key={question.id}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Chip label={index + 1} size="small" />
                    <Chip
                      label={question.difficulty}
                      size="small"
                      color={
                        question.difficulty === 'easy'
                          ? 'success'
                          : question.difficulty === 'hard'
                          ? 'error'
                          : 'warning'
                      }
                    />
                    <Typography noWrap sx={{ flexGrow: 1 }}>
                      {question.question}
                    </Typography>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => handleOpenEditQuestion(question, e)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteQuestion(question.id);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Pergunta:
                      </Typography>
                      <Typography>{question.question}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Resposta Esperada:
                      </Typography>
                      <Typography>
                        {question.expected_answer || 'Não definida'}
                      </Typography>
                    </Grid>
                    {question.context && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Contexto:
                        </Typography>
                        <Typography variant="body2">{question.context}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <GenerateIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography color="textSecondary" gutterBottom>
              Nenhuma pergunta gerada ainda
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Vincule fontes de dados e gere perguntas sintéticas usando IA, ou adicione perguntas manualmente
            </Typography>
            <Box display="flex" gap={2} justifyContent="center">
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateQuestion}
              >
                Adicionar Pergunta
              </Button>
              <Button
                variant="contained"
                startIcon={<GenerateIcon />}
                onClick={() => setGenerateDialogOpen(true)}
                disabled={!base.file_sources?.length}
              >
                Gerar Perguntas
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Generate Questions Dialog */}
      <Dialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Gerar Perguntas Sintéticas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={3}>
            As perguntas serão geradas usando Claude Sonnet via Amazon Bedrock,
            baseadas no conteúdo dos arquivos das fontes de dados vinculadas.
          </Typography>
          {base.file_sources && base.file_sources.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                Fontes de dados que serão utilizadas:
              </Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {base.file_sources.map((fs) => (
                  <Chip
                    key={fs.id}
                    icon={<FolderIcon />}
                    label={`${fs.name} (${fs.files_count || 0} arquivos)`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          <TextField
            label="Quantidade de Perguntas"
            type="number"
            fullWidth
            value={generateForm.num_questions}
            onChange={(e) =>
              setGenerateForm({
                ...generateForm,
                num_questions: Math.min(100, Math.max(1, parseInt(e.target.value) || 1)),
              })
            }
            inputProps={{ min: 1, max: 100 }}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Dificuldade</InputLabel>
            <Select
              value={generateForm.difficulty}
              label="Dificuldade"
              onChange={(e) =>
                setGenerateForm({ ...generateForm, difficulty: e.target.value })
              }
            >
              <MenuItem value="easy">Fácil</MenuItem>
              <MenuItem value="medium">Médio</MenuItem>
              <MenuItem value="hard">Difícil</MenuItem>
              <MenuItem value="mixed">Misto</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Prompt / Instruções para Geração"
            multiline
            rows={6}
            fullWidth
            value={generateForm.custom_instructions}
            onChange={(e) =>
              setGenerateForm({ ...generateForm, custom_instructions: e.target.value })
            }
            placeholder="Ex: Foque em perguntas sobre processos de negócio, inclua perguntas sobre datas e prazos, evite perguntas muito técnicas..."
            helperText="Adicione instruções específicas para guiar a geração das perguntas. Este prompt será salvo na base de perguntas."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleGenerateQuestions}
            variant="contained"
            disabled={generating}
          >
            {generating ? <CircularProgress size={24} /> : 'Gerar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Configurações da Base de Perguntas</DialogTitle>
        <DialogContent>
          <TextField
            label="Nome"
            fullWidth
            required
            value={settingsForm.name}
            onChange={(e) =>
              setSettingsForm({ ...settingsForm, name: e.target.value })
            }
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            label="Descrição"
            fullWidth
            multiline
            rows={3}
            value={settingsForm.description}
            onChange={(e) =>
              setSettingsForm({ ...settingsForm, description: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Fontes de Dados</InputLabel>
            <Select
              multiple
              value={settingsForm.file_source_ids}
              onChange={(e) =>
                setSettingsForm({ ...settingsForm, file_source_ids: e.target.value })
              }
              input={<OutlinedInput label="Fontes de Dados" />}
              renderValue={(selected) =>
                selected
                  .map((sid) => {
                    const fs = allFileSources.find((s) => s.id === sid);
                    return fs ? fs.name : sid;
                  })
                  .join(', ')
              }
            >
              {allFileSources.map((source) => (
                <MenuItem key={source.id} value={source.id}>
                  <Checkbox checked={settingsForm.file_source_ids.indexOf(source.id) > -1} />
                  <ListItemText
                    primary={source.name}
                    secondary={`${source.files?.length || 0} arquivos`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSaveSettings}
            variant="contained"
            disabled={savingSettings || !settingsForm.name.trim()}
          >
            {savingSettings ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Question Dialog */}
      <Dialog
        open={questionDialogOpen}
        onClose={() => setQuestionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingQuestion ? 'Editar Pergunta' : 'Adicionar Pergunta'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Pergunta"
            fullWidth
            required
            multiline
            rows={3}
            value={questionForm.question}
            onChange={(e) =>
              setQuestionForm({ ...questionForm, question: e.target.value })
            }
            sx={{ mt: 2, mb: 2 }}
            placeholder="Digite a pergunta que será feita ao agente..."
          />
          <TextField
            label="Resposta Esperada"
            fullWidth
            multiline
            rows={4}
            value={questionForm.expected_answer}
            onChange={(e) =>
              setQuestionForm({ ...questionForm, expected_answer: e.target.value })
            }
            sx={{ mb: 2 }}
            placeholder="Digite a resposta esperada para avaliação..."
            helperText="A resposta esperada será usada para avaliar a qualidade da resposta do agente"
          />
          <TextField
            label="Contexto"
            fullWidth
            multiline
            rows={3}
            value={questionForm.context}
            onChange={(e) =>
              setQuestionForm({ ...questionForm, context: e.target.value })
            }
            sx={{ mb: 2 }}
            placeholder="Contexto adicional relevante para a pergunta..."
            helperText="Informações de contexto que ajudam a entender a pergunta"
          />
          <FormControl fullWidth>
            <InputLabel>Dificuldade</InputLabel>
            <Select
              value={questionForm.difficulty}
              label="Dificuldade"
              onChange={(e) =>
                setQuestionForm({ ...questionForm, difficulty: e.target.value })
              }
            >
              <MenuItem value="easy">Fácil</MenuItem>
              <MenuItem value="medium">Médio</MenuItem>
              <MenuItem value="hard">Difícil</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuestionDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSaveQuestion}
            variant="contained"
            disabled={savingQuestion || !questionForm.question.trim()}
          >
            {savingQuestion ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QuestionBaseDetail;
